import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { ConfigProvider, theme } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import esES from 'antd/locale/es_ES';

dayjs.locale('es');

interface ThemeSettings {
    darkMode: boolean;
    primaryColor: string;
}

interface ThemeContextType extends ThemeSettings {
    setDarkMode: (darkMode: boolean) => void;
    setPrimaryColor: (color: string) => void;
}

const defaultSettings: ThemeSettings = {
    darkMode: false,
    primaryColor: '#1890ff', // Default Ant Design blue, will be overwritten by effect if needed
};

const ThemeContext = createContext<ThemeContextType>({
    ...defaultSettings,
    setDarkMode: () => { },
    setPrimaryColor: () => { },
});

export const useTheme = () => useContext(ThemeContext);

interface ThemeProviderProps {
    children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const [darkMode, setDarkModeState] = useState<boolean>(defaultSettings.darkMode);
    const [primaryColor, setPrimaryColorState] = useState<string>(defaultSettings.primaryColor);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        const storedSettings = localStorage.getItem('theme_settings');
        if (storedSettings) {
            try {
                const parsed = JSON.parse(storedSettings);
                setDarkModeState(parsed.darkMode ?? defaultSettings.darkMode);
                setPrimaryColorState(parsed.primaryColor ?? defaultSettings.primaryColor);
            } catch (e) {
                console.error("Failed to parse theme settings", e);
            }
        } else {
            // Try to get default color from CSS variable if available, or stick to default
            const style = getComputedStyle(document.body);
            const cssColor = style.getPropertyValue("--theme-color").trim();
            if (cssColor) {
                setPrimaryColorState(cssColor);
            }
        }
        setIsLoaded(true);
    }, []);

    useEffect(() => {
        if (isLoaded) {
            localStorage.setItem('theme_settings', JSON.stringify({ darkMode, primaryColor }));
        }
        // Sync with CSS variable for custom styles
        document.body.style.setProperty('--theme-color', primaryColor);

        if (darkMode) {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }
    }, [darkMode, primaryColor, isLoaded]);

    const setDarkMode = (value: boolean) => {
        setDarkModeState(value);
    };

    const setPrimaryColor = (value: string) => {
        setPrimaryColorState(value);
    };

    return (
        <ThemeContext.Provider value={{ darkMode, primaryColor, setDarkMode, setPrimaryColor }}>
            <ConfigProvider
                locale={esES}
                theme={{
                    algorithm: darkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
                    token: {
                        colorPrimary: primaryColor,
                    },
                }}
            >
                {children}
            </ConfigProvider>
        </ThemeContext.Provider>
    );
};
